<!-- Three, two, one.

     WHY THIS EXISTS. Last Gasp starts a sixty-second clock and Endurance
     starts a nine-second one, and both used to start on the same frame as the
     tap. His thumb is still on the Start button and his eyes are still on it,
     so the first question of every run was answered late through no fault of
     his - and in Endurance, where the score IS the time, that came off the
     personal best every single run.

     It also does the job a countdown does in every other game: it tells you to
     get ready, which is most of what makes the first question feel fair.

     GO IS NOT ONE OF THE NUMBERS. It used to run the identical 300ms pop as
     three, two and one, which made the one beat in the sequence that says
     something different look exactly like the two before it. It gets the brand
     yellow, an overshoot and a ring thrown off it (app.css).

     AND IT IS SKIPPABLE, from the second run on. He knows what three-two-one
     means by then and the 2.6 seconds is a toll gate. Tapping does NOT fire
     `go` on the spot — that would reintroduce the exact bug this component
     exists to fix — it jumps to the GO beat, which still gets its own frame
     before the clock starts. -->
<template>
  <div
    v-if="showing"
    class="countdown"
    role="status"
    aria-live="assertive"
    @click="skip"
  >
    <div :key="n" class="n" :class="{ go: n <= 0 }">
      {{ n > 0 ? n : 'GO' }}
      <span class="ring" aria-hidden="true"></span>
    </div>
    <div class="k">{{ label }}</div>
    <!-- Rendered for the whole countdown, hidden rather than removed at GO:
         see the note on `.countdown .skip.spent` in app.css. -->
    <p v-if="hint" class="skip" :class="{ spent: n <= 0 }">Tap to skip</p>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue';
import { haptic } from '../core/util.js';
import { showUntilLearned } from '../core/ui';

const props = defineProps({
  /** Flip to true to run it. */
  when: { type: Boolean, default: false },
  from: { type: Number, default: 3 },
  label: { type: String, default: 'Get ready' },
  /** Milliseconds per number. */
  step: { type: Number, default: 650 },
});

const emit = defineEmits(['go']);

const showing = ref(false);
const hint = ref(false);
const n = ref(props.from);
let timer = null;

function stop() {
  clearTimeout(timer);
  timer = null;
}

function tick() {
  if (n.value > 0) {
    haptic('tick');
    timer = setTimeout(() => {
      n.value -= 1;
      tick();
    }, props.step);
    return;
  }
  // "GO" gets its own beat, then the clock starts. Starting on the same frame
  // as the word appears is the bug this whole component exists to fix.
  haptic('pop');
  timer = setTimeout(() => {
    showing.value = false;
    emit('go');
  }, props.step * 0.7);
}

/** Straight to the GO beat. Never straight to `go` — see the header. */
function skip() {
  if (!showing.value || n.value <= 0) return;
  stop();
  n.value = 0;
  tick();
}

watch(
  () => props.when,
  (on) => {
    if (!on) return;
    stop();
    n.value = props.from;
    showing.value = true;
    // Off on the very first run, where the ceremony is the point; on for runs
    // two to five; off again once he has it. Same two-counter pattern as the
    // AUS bonus — `showUntilLearned` is true for the first N calls per key.
    const first = showUntilLearned('countdown.first', 1);
    const stillHinting = showUntilLearned('countdown.skip-hint', 5);
    hint.value = !first && stillHinting;
    tick();
  },
);

onUnmounted(stop);
</script>
